import { type DollarAmountField, type Field, type FieldMap } from '../../../types/forms/fields'
import { CPTCode, CPTCodeGroup, isCptCodeGroup } from '../../../types/providers'
import { FieldTypes } from '../../FieldTypes'
import { antepartumCodes } from './antepartum'
import { deliveryCodes } from './delivery'
import { gynCptCodes } from './gyn'
import { laborCodes } from './labor'
import { lactationCodes } from './lactation'
import { newbornCodes } from './newborn'
import { otherCodes } from './other'
import { postpartumCodes } from './postpartum'
import { problemCodes } from './problem'

type CptCodeGroupId =
  | 'delivery'
  | 'antepartum'
  | 'postpartum'
  | 'labor'
  | 'newborn'
  | 'lactation'
  | 'gyn'
  | 'problem'
  | 'other'

export const cptCodes: Record<CptCodeGroupId, CPTCodeGroup> = {
  delivery: deliveryCodes,
  antepartum: antepartumCodes,
  postpartum: postpartumCodes,
  labor: laborCodes,
  newborn: newbornCodes,
  lactation: lactationCodes,
  gyn: gynCptCodes,
  problem: problemCodes,
  other: otherCodes,
}

export const allCptCodes = Object.values(cptCodes ?? {}).reduce<Array<CPTCode>>((acc, curr) => {
  if (isCptCodeGroup(curr)) return [...acc, ...curr.codes]
  return [...acc, curr]
}, [])

export const flattenedCptCodes = Object.entries(cptCodes).reduce<Record<string, CPTCode>>(
  (acc, [_, value]) => ({
    ...acc,
    ...value.codes.reduce<Record<string, CPTCode>>(
      (groupAcc, curr) => ({ ...groupAcc, [curr.code]: curr }),
      {},
    ),
  }),
  {},
)

export const getCodeChargeField = (codeData: CPTCode): DollarAmountField => {
  const { code, description, notes } = codeData ?? {}
  const field: Field = {
    placeholder: `${code} - ${description}`,
    type: FieldTypes.DOLLAR_AMOUNT,
    optional: true,
  }
  if (notes) field.hints = [{ text: notes }]
  return field
}

export const getChargeCodeFields = (codes: Array<CPTCode>): Record<string, DollarAmountField> =>
  codes.reduce(
    (acc, curr) => ({
      ...acc,
      [`_${curr.code}`]: getCodeChargeField(curr),
    }),
    {},
  )

export const getCptCodeGroupChargeFields = (group: CPTCodeGroup): FieldMap => {
  const { name, codes } = group
  return {
    name: `${name} Codes`,
    children: getChargeCodeFields(codes),
  }
}

export const groupedCptCodeChargeFields = Object.entries(cptCodes).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: getCptCodeGroupChargeFields(value),
  }),
  {} as Record<CptCodeGroupId, FieldMap>,
)

export const codeGroups: Record<string, CptCodeGroupId[]> = Object.entries(cptCodes).reduce<
  Record<string, CptCodeGroupId[]>
>((acc, [key, value]) => {
  value.codes.forEach(code => {
    const codeArr = acc[`_${code.code}`] ?? []
    acc[`_${code.code}`] = [...codeArr, key as CptCodeGroupId]
  })
  return acc
}, {})

export const groupCptCodeCharges = (values: Record<string, number>) =>
  Object.entries(values).reduce(
    (acc, [key, value]) => {
      const groupIds = codeGroups[key] ?? []
      groupIds.forEach(groupId => {
        if (!acc[groupId]) acc[groupId] = {}
        acc[groupId][key] = value
      })
      return acc
    },
    {} as Record<CptCodeGroupId, Record<string, number>>,
  )

export const ungroupCptCodeCharges = (values: Record<CptCodeGroupId, Record<string, number>>) =>
  Object.entries(values).reduce<Record<string, number>>((acc, [_, value]) => {
    Object.entries(value).forEach(([code, charge]) => {
      acc[`_${code}`] = charge
    })
    return acc
  }, {})
